import { Controller } from "@hotwired/stimulus";
import { setTriggerExpanded } from "../../utils/disclosure.js";

/**
 * GO4S Design System — MultiSelect (chips + dropdown à cases).
 * Compatible formulaire : un input hidden name[]=value par sélection.
 * Spec : docs/design-system/REFERENCE.md §3
 */
export default class extends Controller {
  static targets = ["control", "panel", "option", "placeholder", "input", "trigger"];
  static values = { name: String };

  connect() {
    this.onDoc = this.onDoc.bind(this);
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onDoc);
  }

  setExpanded(v) {
    setTriggerExpanded(this, v);
  }

  toggle(e) {
    if (e && e.target.closest(".ds-multiselect__chip-x")) return;
    this.panelTarget.hidden ? this.open() : this.close();
  }

  open() {
    this.panelTarget.hidden = false;
    this.element.classList.add("is-open");
    this.setExpanded(true);
    document.addEventListener("mousedown", this.onDoc);
  }

  close() {
    this.panelTarget.hidden = true;
    this.element.classList.remove("is-open");
    this.setExpanded(false);
    document.removeEventListener("mousedown", this.onDoc);
    // The form hears one change once the list closes, not one per box ticked:
    // a filter form that searches live sends a single search for the choice.
    if (this.changed) {
      this.changed = false;
      this.element.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  onDoc(e) {
    if (!this.element.contains(e.target)) this.close();
  }

  choose(e) {
    const btn = e.currentTarget;
    const selected = btn.getAttribute("aria-selected") === "true";
    btn.setAttribute("aria-selected", String(!selected));
    this.sync();
  }

  remove(e) {
    e.stopPropagation();
    const value = e.currentTarget.dataset.value;
    const opt = this.optionTargets.find((o) => o.dataset.value === value);
    if (opt) opt.setAttribute("aria-selected", "false");
    this.sync();
    // A chip removed with the list closed: its change is heard at once.
    if (this.panelTarget.hidden) {
      this.changed = false;
      this.element.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // Reconstruit chips + inputs hidden à partir des options sélectionnées.
  sync() {
    this.changed = true;
    const selected = this.optionTargets.filter((o) => o.getAttribute("aria-selected") === "true");
    this.controlTarget
      .querySelectorAll(".ds-multiselect__chip, [data-ds-multiselect-target='input']")
      .forEach((n) => n.remove());

    const placeholder = this.hasPlaceholderTarget ? this.placeholderTarget : null;
    selected.forEach((o) => {
      const chip = document.createElement("span");
      chip.className = "ds-multiselect__chip";
      chip.dataset.value = o.dataset.value;
      chip.innerHTML =
        `${o.dataset.label}` +
        `<button type="button" class="ds-multiselect__chip-x" data-action="ds-multiselect#remove" ` +
        `data-value="${o.dataset.value}" aria-label="Retirer"><i data-lucide="x" class="ds-icon"></i></button>`;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.nameValue;
      input.value = o.dataset.value;
      input.dataset.dsMultiselectTarget = "input";

      this.controlTarget.insertBefore(chip, placeholder);
      this.controlTarget.insertBefore(input, placeholder);
    });

    if (placeholder) placeholder.style.display = selected.length ? "none" : "";
    if (window.g4sRenderIcons) window.g4sRenderIcons();
  }
}
